/**
 * Presentaciones y claves de carrito del rediseño (client-safe).
 *
 * El carrito guarda `Record<clave, cantidad>` donde la clave codifica el
 * producto y su presentación:
 *   "slug"          → unidad suelta (cantidad = unidades)
 *   "slug|pack7"    → pack de 7      (cantidad = packs)
 *   "slug|pack14"   → pack de 14     (cantidad = packs)
 *   "slug|mix7"     → unidades de ese sabor dentro de un pack VARIADO de 7
 *   "slug|mix14"    → ídem para variado de 14
 * Las unidades "mix" se cobran al precio prorrateado del pack (pack/N) y el
 * servidor valida que sumen múltiplos de 7 o 14.
 */

import type { ProductoApp } from "./tipos";

export type Pres = "unidad" | "pack7" | "pack14" | "mix7" | "mix14";

export function clave(slug: string, pres: Pres): string {
  return pres === "unidad" ? slug : `${slug}|${pres}`;
}

export function parseClave(claveCarrito: string): { slug: string; pres: Pres } {
  const [slug, sufijo] = claveCarrito.split("|");
  const validas: string[] = ["pack7", "pack14", "mix7", "mix14"];
  const pres: Pres = sufijo && validas.includes(sufijo) ? (sufijo as Pres) : "unidad";
  return { slug, pres };
}

/** Precio de la presentación en la lista del producto, con fallback ×n. */
export function precioPresentacion(
  producto: Pick<ProductoApp, "precio" | "presentaciones">,
  id: "pack7" | "pack14",
): number | null {
  const n = id === "pack7" ? 7 : 14;
  const fila = producto.presentaciones.find((p) => p.id === id);
  if (fila?.precio != null) return fila.precio;
  return producto.precio === null ? null : Math.round(producto.precio * n * 100) / 100;
}

/** Precio UNITARIO efectivo de una clave de carrito (por unidad o por pack). */
export function precioClave(
  producto: Pick<ProductoApp, "precio" | "presentaciones">,
  pres: Pres,
): number | null {
  switch (pres) {
    case "unidad":
      return producto.precio;
    case "pack7":
      return precioPresentacion(producto, "pack7");
    case "pack14":
      return precioPresentacion(producto, "pack14");
    case "mix7": {
      const p = precioPresentacion(producto, "pack7");
      return p === null ? null : p / 7;
    }
    case "mix14": {
      const p = precioPresentacion(producto, "pack14");
      return p === null ? null : p / 14;
    }
  }
}

/** Etiqueta corta de la presentación para el carrito/pedido. */
export function etiquetaPres(pres: Pres): string {
  switch (pres) {
    case "unidad":
      return "";
    case "pack7":
      return "Pack 7";
    case "pack14":
      return "Pack 14";
    case "mix7":
      return "Pack variado (7)";
    case "mix14":
      return "Pack variado (14)";
  }
}

/** Unidades físicas que representa 1 de cantidad en esa clave. */
export function unidadesPor(pres: Pres): number {
  if (pres === "pack7") return 7;
  if (pres === "pack14") return 14;
  return 1;
}
